/**
 * 主界面
 *
 * 布局：
 *   ┌─────────────── 顶部工具栏 ───────────────┐
 *   ├──────────────────────────────────────────┤
 *   │              牌桌（四家）                  │
 *   ├──────────────────────────────────────────┤
 *   │              操作栏（打牌/立直/和了）        │
 *   └──────────────────────────────────────────┘
 */

import { useCallback, useEffect, useState } from 'react';
import { Table } from './components/Table.js';
import { useGame } from './hooks/useGame.js';
import { useGameDriver } from './hooks/useGameDriver.js';
import { canTsumo } from '@mahjong/engine/src/game/rules.js';
import { ResultOverlay } from './components/ResultOverlay.js';
import { ResponseBar } from './components/ResponseBar.js';

export function App() {
  const game = useGame({ roundWind: 'east' });
  const { state, players, center, selfSeat, log, isSelfTurn, hints, hintText, kanOptions, kan } =
    game;

  // 出牌两下确认：第一下选中（牌抬高），再点同一张才打出（减少误触）
  const [selectedPos, setSelectedPos] = useState<number | null>(null);
  useEffect(() => {
    setSelectedPos(null); // 引擎状态一变（摸牌/出牌/鸣牌）即清除选中
  }, [state]);

  // 对局记录：默认隐藏，工具栏按钮切换显隐
  const [showLog, setShowLog] = useState(false);

  const handleTileClick = (pos: number, tileIdx: number) => {
    if (selectedPos === pos) {
      game.discard(tileIdx);
      setSelectedPos(null);
    } else {
      setSelectedPos(pos);
    }
  };

  // AI 驱动（他家自动行动）
  //
  // ⚠️ onLog 必须 useCallback 记忆 —— 虽是 ref 传递，但保持稳定避免不必要渲染。
  const onLog = useCallback((msg: string) => game.pushLog(msg), [game.pushLog]);

  // ⚠️ 暂停条件必须来自 useGame.pendingResponse 这唯一判定源（== 响应栏的渲染条件）。
  //    早前这里另外算一套（碰只看手牌数、不吃 canApply），与响应栏不一致 → 死锁。
  useGameDriver({
    state,
    selfSeat,
    enabled: state.phase !== 'over',
    setState: game.setState,
    onLog,
    selfResponsePending: !!game.pendingResponse,
  });

  const isOver = state.phase === 'over';
  const result = state.result;

  // 自家可执行动作（仅可操作时渲染；放自家手牌上一行右侧，不在中间）
  const selfActions = (
    <>
      {kanOptions.map((opt, i) => (
        <button key={i} className="btn btn-primary" onClick={() => kan(opt)}>
          {opt.label}
        </button>
      ))}
      {canTsumo(state).legal && (
        <button className="btn btn-primary" onClick={game.tsumo}>
          自摸和了
        </button>
      )}
      {game.canRiichiNow && (
        <button
          className="btn btn-accent"
          onClick={game.riichi}
          title="宣言立直并打出推荐牌"
        >
          立直
        </button>
      )}
    </>
  );

  return (
    <div className="app">
      {/* 顶部工具栏 */}
      <header className="toolbar">
        <div className="toolbar-left">
          <h1 className="title">立直麻将</h1>
          <span className="subtitle">单机版 · 4 家 AI</span>
        </div>
        <div className="toolbar-right">
          <label className="hint-toggle">
            <input
              type="checkbox"
              checked={game.showHints}
              onChange={(e) => game.setShowHints(e.target.checked)}
            />
            显示提示
          </label>
          <button className="btn btn-secondary" onClick={() => setShowLog((v) => !v)}>
            对局记录
          </button>
          <button className="btn btn-secondary" onClick={() => game.newGame(false)}>
            新对局
          </button>
        </div>
      </header>

      {/* 牌桌 */}
      <main className={`table-wrap ${isSelfTurn ? 'my-turn' : ''}`}>
        <Table
          players={players}
          center={center}
          selfSeat={selfSeat}
          onClickTile={isSelfTurn ? handleTileClick : undefined}
          selectedHandPos={selectedPos}
          showHints={game.showHints}
          hintIndices={hints}
          selfActions={selfActions}
        />
      </main>

      {/* 鸣牌/荣和响应栏（等玩家决策）*/}
      {game.pendingResponse && (
        <ResponseBar
          discardTile={game.pendingResponse.discardTile}
          discarderName={game.pendingResponse.discarderName}
          options={game.pendingResponse.options}
          onSelect={game.respond}
          onPass={game.passResponse}
        />
      )}

      {/* 操作栏 */}
      <footer className="action-bar">
        <div className="action-left">
          {isSelfTurn ? (
            <span className="turn-indicator turn-mine">▶ 轮到你出牌</span>
          ) : (
            <span className="turn-indicator">
              等待 {['自家', '下家', '对家', '上家'][center.activeSeat]} 出牌…
            </span>
          )}
        </div>

        <div className="action-center" />

        <div className="action-right">
          {game.showHints && hintText && (
            <div className="hint-box">
              <div className="hint-line">
                <span className="hint-label">推荐</span>
                <span className="hint-value hint-strong">{hintText.recommend}</span>
                <span className="hint-label">向听</span>
                <span className="hint-value">{hintText.shanten}</span>
              </div>
              {hintText.waits && (
                <div className="hint-line">
                  <span className="hint-label">听牌</span>
                  <span className="hint-value">{hintText.waits}</span>
                </div>
              )}
              <div className="hint-line">
                <span className="hint-label">判断</span>
                <span className="hint-value hint-dim">{hintText.advice}</span>
              </div>
            </div>
          )}
        </div>
      </footer>

      {/* 日志（默认隐藏，工具栏「对局记录」按钮切换） */}
      {showLog && (
        <aside className="log-panel">
          <div className="log-title">对局记录</div>
          <ul className="log-list">
            {log.slice(0, 12).map((l, i) => (
              <li key={i} className="log-item">
                {l}
              </li>
            ))}
          </ul>
        </aside>
      )}

      {/* 结算遮罩 */}
      {isOver && result && (
        <ResultOverlay
          state={state}
          onNewGame={() => game.newGame(true)}
        />
      )}
    </div>
  );
}

/** 牌名 → 索引偏移（简易解析） */
function suitOffset(name: string): number {
  const suit = name[name.length - 1];
  const base = suit === 'm' ? 0 : suit === 'p' ? 9 : suit === 's' ? 18 : 27;
  return base;
}
